/**
 * apps/command/src/screens/KioskDetail.tsx — §16 screen 6, P0 subset.
 *
 * §15.9's "drill-down everywhere": every number on a tile leads to the rows
 * behind it. "A figure a user cannot interrogate is a figure they will not
 * trust."
 *
 * Telemetry, peripherals, the raw history, and §4.5's remote actions with their
 * command trail. Live camera/screen is P5 and genuinely absent — and absent
 * rather than disabled, because §15.7's rule against showing an official a
 * control that can only fail applies here as much as on the rail.
 *
 * `RemoteActions` renders its HISTORY for anyone with `mbt.fleet.read` and its
 * CONTROLS only for `mbt.command.issue` (governor + operator, D-MBT-04). A
 * technician can see every action taken on a kiosk and issue none: an incident
 * review that hides the actions taken is not a review.
 *
 * PERIPHERALS ARE THREE-STATE, not two. `true`, `false` and "the kiosk did not
 * report this field" are different facts: an older build that does not send
 * `speaker` must not be rendered as a kiosk with no speaker, because §4.4 turns
 * a missing camera into a CRITICAL alert and a technician would be dispatched
 * to a machine that is fine.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { deriveKioskStatus } from '@navoiy/domain';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { MaintenanceAndAsset } from '../components/MaintenanceAndAsset.jsx';
import { RemoteActions } from '../components/RemoteActions.jsx';
import { t } from '../i18n/index.js';
import {
  ageLabel,
  durationParts,
  flag,
  integer,
  percent,
  reading,
  stamp,
  text,
} from '../lib/format.js';
import {
  EmptyState,
  Notice,
  Panel,
  PanelHeader,
  Readout,
  Skeleton,
  StatusPill,
} from '../ui/index.jsx';
import { IconBack } from '../ui/icons.jsx';
import { Sparkline } from '../ui/charts.jsx';

type MbtKiosk = components['schemas']['MbtKiosk'];
interface KioskDetailResponse extends MbtKiosk {
  history: { at: string; payload: unknown }[];
}

/**
 * The four readings worth a curve, with the threshold each is judged against.
 *
 * Chosen because each one predicts a different failure: disk fills and the
 * queue stops draining, CPU pins and the screen stutters, latency climbs and
 * submissions time out, temperature rises and the machine throttles itself.
 * The rest of `KioskTelemetry` is state rather than trend and belongs in the
 * readouts above, not in a chart.
 */
const TRACES = [
  {
    key: 'disk',
    group: 'health',
    field: 'disk_free_pct',
    labelKey: 'fleet.metric.disk',
    unitKey: 'unit.percent',
    tone: 'text-brand',
    reference: 20,
  },
  {
    key: 'cpu',
    group: 'health',
    field: 'cpu_pct',
    labelKey: 'fleet.metric.cpu',
    unitKey: 'unit.percent',
    tone: 'text-gold',
    reference: 85,
  },
  {
    key: 'rtt',
    group: 'network',
    field: 'rtt_ms',
    labelKey: 'fleet.metric.rtt',
    unitKey: 'unit.ms',
    tone: 'text-brand',
    reference: 400,
  },
  {
    key: 'temp',
    group: 'health',
    field: 'cpu_temp_c',
    labelKey: 'fleet.metric.temp',
    unitKey: 'unit.degrees',
    tone: 'text-gold',
    reference: 70,
  },
] as const;

export function KioskDetail(): React.JSX.Element {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);

  const [kiosk, setKiosk] = useState<KioskDetailResponse | null>(null);
  const [error, setError] = useState<'forbidden' | 'notFound' | 'network' | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!deviceId) return;
    try {
      const data = unwrap(
        await api().GET('/mbt/fleet/kiosks/{device_id}', {
          params: { path: { device_id: deviceId } },
        }),
      );
      setKiosk(data as KioskDetailResponse);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) setError('forbidden');
      else if (caught instanceof ApiError && caught.status === 404) setError('notFound');
      else setError('network');
    }
  }, [deviceId, onExpired]);

  useEffect(() => {
    if (!deviceId) return undefined;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const data = unwrap(
          await api().GET('/mbt/fleet/kiosks/{device_id}', {
            params: { path: { device_id: deviceId } },
          }),
        );
        if (!cancelled) {
          setKiosk(data as KioskDetailResponse);
          setError(null);
        }
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) onExpired();
        else if (caught instanceof ApiError && caught.status === 403) setError('forbidden');
        else if (caught instanceof ApiError && caught.status === 404) setError('notFound');
        else setError('network');
      }
    };
    void poll();
    // §15.9 "live without refresh" — the detail screen ticks like the grid.
    const timer = setInterval(() => void poll(), 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [deviceId, onExpired]);

  if (error === 'forbidden') return <Notice tone="crit">{t('error.forbidden')}</Notice>;
  if (error === 'notFound') return <Notice tone="warn">{t('error.notFound')}</Notice>;
  if (!kiosk) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const telemetryAt = kiosk.telemetry_at ? new Date(kiosk.telemetry_at) : null;
  const status = deriveKioskStatus({
    telemetryAt,
    maintenanceUntil: kiosk.maintenance_until ? new Date(kiosk.maintenance_until) : null,
    now,
  });
  const uptime = durationParts(reading(kiosk.telemetry, 'uptime_s'));

  return (
    <div className="space-y-6">
      <Link
        to="/fleet"
        className="flex items-center gap-1.5 text-micro text-ink-500 transition-colors duration-state hover:text-ink-300"
      >
        <IconBack className="h-4 w-4" />
        {t('fleet.back')}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-hero font-semibold tabular-nums">{kiosk.short_code}</h2>
          <p className="text-label text-ink-300">{kiosk.label}</p>
          <p className="text-micro text-ink-500">{kiosk.mahalla_name}</p>
        </div>
        <div className="text-right">
          <StatusPill status={status} label={t(`status.${status}`)} />
          <p className="mt-2 text-micro tabular-nums text-ink-500">
            {telemetryAt ? t('app.asOf', { at: ageLabel(telemetryAt, now, t) }) : t('app.never')}
          </p>
        </div>
      </div>

      {error === 'network' ? <Notice tone="warn">{t('error.network')}</Notice> : null}
      {kiosk.maintenance_until ? (
        <Notice tone="quiet">
          {t('fleet.maintenanceUntil', { until: stamp(new Date(kiosk.maintenance_until)) })}
        </Notice>
      ) : null}

      {kiosk.telemetry === undefined ? (
        <EmptyState message={kiosk.enrolled ? t('fleet.noTelemetry') : t('fleet.notEnrolled')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel>
            <PanelHeader title={t('fleet.telemetry')} />
            <div className="divide-y divide-hairline px-4 py-2">
              <Readout
                label={t('fleet.metric.disk')}
                value={percent(reading(kiosk.telemetry, 'health', 'disk_free_pct'))}
                absent={t('app.unknown')}
                unit={t('unit.percent')}
              />
              <Readout
                label={t('fleet.metric.cpu')}
                value={percent(reading(kiosk.telemetry, 'health', 'cpu_pct'))}
                absent={t('app.unknown')}
                unit={t('unit.percent')}
              />
              <Readout
                label={t('fleet.metric.ram')}
                value={integer(reading(kiosk.telemetry, 'health', 'ram_used_mb'))}
                absent={t('app.unknown')}
                unit={t('unit.mb')}
              />
              <Readout
                label={t('fleet.metric.temp')}
                value={integer(reading(kiosk.telemetry, 'health', 'cpu_temp_c'))}
                absent={t('app.unknown')}
                unit={t('unit.celsius')}
              />
              <Readout
                label={t('fleet.metric.rtt')}
                value={integer(reading(kiosk.telemetry, 'network', 'rtt_ms'))}
                absent={t('app.unknown')}
                unit={t('unit.ms')}
              />
              <Readout
                label={t('fleet.metric.uptime')}
                value={uptime ? String(uptime.n) : null}
                absent={t('app.unknown')}
                unit={uptime ? ` ${t(uptime.key)}` : undefined}
              />
              <Readout
                label={t('fleet.metric.appealsToday')}
                value={integer(reading(kiosk.telemetry, 'work', 'appeals_today'))}
                absent={t('app.unknown')}
              />
              <Readout
                label={t('fleet.metric.queue')}
                value={integer(reading(kiosk.telemetry, 'work', 'queue_depth'))}
                absent={t('app.unknown')}
              />
              <Readout
                label={t('fleet.metric.version')}
                value={text(kiosk.telemetry, 'app_version') ?? kiosk.app_version ?? null}
                absent={t('app.unknown')}
              />
            </div>
          </Panel>

          <Panel>
            <PanelHeader title={t('fleet.peripherals.title')} />
            <div className="divide-y divide-hairline px-4 py-2">
              {(['camera', 'mic', 'speaker', 'touch'] as const).map((device) => {
                const present = flag(kiosk.telemetry, 'peripherals', device);
                return (
                  <Readout
                    key={device}
                    label={t(`fleet.peripherals.${device}`)}
                    // Three states, not two — see the file header.
                    value={
                      present === null
                        ? null
                        : present
                          ? t('fleet.peripherals.present')
                          : t('fleet.peripherals.absent')
                    }
                    absent={t('app.unknown')}
                  />
                );
              })}
            </div>
          </Panel>
        </div>
      )}

      {/* §4.5 — remote actions and their history. Above the telemetry history
          because an operator on this screen is usually here to DO something,
          and the readings above already told them why. */}
      <RemoteActions deviceId={kiosk.id} shortCode={kiosk.short_code} onExpired={onExpired} />

      {/* §4.8 maintenance windows and §4.7's asset record. Below the actions
          because an operator here to DO something is usually here to restart,
          not to write down a serial number. */}
      <MaintenanceAndAsset kiosk={kiosk} onChanged={() => void load()} onExpired={onExpired} />

      {/*
        ======================================================================
        TWENTY-FOUR HOURS OF TELEMETRY, AS CURVES. It was a list of numbers.
        ======================================================================

        `getMbtFleetKiosk` has always returned up to 24 h of raw samples at
        ~30 s spacing, and this screen printed the newest forty of them as rows
        of figures — the one presentation that makes a trend invisible. A
        technician's question here is never «what was the disk at 14:32», it is
        «is this machine getting worse», and that is a shape.

        Four traces, each on its own scale because they are four different units
        and a shared axis would flatten three of them. They are stacked rather
        than overlaid for the same reason: this is four questions, not one
        chart with four series.

        The samples are IRREGULARLY SPACED and may have gaps — a kiosk that was
        off sends nothing — so these are drawn as sequences of readings, not
        against a time axis. Anything else would silently interpolate across an
        outage and draw a healthy line through the hours a machine was dead.
      */}
      <Panel>
        <PanelHeader
          title={t('fleet.history')}
          aside={t('fleet.historyHint', { n: String(kiosk.history.length) })}
        />
        {kiosk.history.length < 2 ? (
          <p className="px-4 py-6 text-label text-ink-500">{t('fleet.historyEmpty')}</p>
        ) : (
          <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
            {TRACES.map((trace) => {
              /* Oldest → newest: the API returns newest first, and a chart
                 that runs right to left is a chart everybody misreads. */
              const values = [...kiosk.history]
                .reverse()
                .map((entry) => reading(entry.payload, trace.group, trace.field))
                .filter((value): value is number => typeof value === 'number');
              const latest = values.at(-1);
              return (
                <div key={trace.key} className="rounded border border-hairline bg-panel-raised p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-micro font-semibold uppercase tracking-wide text-ink-500">
                      {t(trace.labelKey)}
                    </span>
                    <span className="text-lead font-bold tabular-nums text-ink-100">
                      {latest === undefined ? '—' : `${Math.round(latest)}${t(trace.unitKey)}`}
                    </span>
                  </div>
                  <Sparkline
                    values={values}
                    className={trace.tone}
                    /* The threshold the alert rule actually fires at, drawn as
                       a dotted rule — so «is this bad» is answered by looking
                       rather than by remembering the rule. */
                    reference={trace.reference}
                  />
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
